import type { PromoterProfileSnapshot } from '@/lib/promoterProfileSnapshot';
import { resolveBankProofUrl } from '@/lib/bankingProof';
import { resolvePromoterPhotoUrl } from '@/lib/promoterPhotos';
import { resolveSignedStorageUrl } from '@/lib/supabaseStorage';

export const PROFILE_LINK_ROUTES = {
  baProfile: '/ba/profile',
  baProfileView: '/ba/profile',
  adminPromoterDetail: '/admin/promoters/:id',
} as const;

export const getAdminPromoterDetailRoute = (promoterId: string) =>
  PROFILE_LINK_ROUTES.adminPromoterDetail.replace(':id', promoterId);

const routePresent = (route: string, links: string[]) => {
  if (route.includes(':id')) {
    const prefix = route.split(':id')[0];
    return links.some((link) => link.startsWith(prefix) && link.length > prefix.length);
  }
  return links.includes(route);
};

export const assertProfileLinkCoverage = (
  surface: string,
  links: string[],
  requiredRoutes: string[] = [
    PROFILE_LINK_ROUTES.baProfile,
    PROFILE_LINK_ROUTES.baProfileView,
    PROFILE_LINK_ROUTES.adminPromoterDetail,
  ]
) => {
  const missing = requiredRoutes.filter((route) => !routePresent(route, links));
  if (missing.length) {
    console.warn(`[ProfileLinks] ${surface} missing required routes:`, missing);
  }
  return missing;
};

export type ProfileDocumentLinkKey =
  | 'headshot'
  | 'cv_photo'
  | 'id_document'
  | 'passport_document'
  | 'work_permit_document'
  | 'cv_document'
  | 'bank_proof'
  | 'matric_certificate'
  | 'highest_qualification_certificate'
  | 'account_holder_authorisation'
  | 'onboarding_contract_pdf'
  | 'onboarding_contract_signature'
  | 'latest_signed_contract_pdf';

export type ProfileDocumentLink = {
  key: ProfileDocumentLinkKey;
  label: string;
  url: string | null;
  state: 'present' | 'missing';
};

const LEGAL_SIGNATURES_BUCKET = 'legal-signatures';
const PROMOTER_IDS_BUCKET = 'promoter-ids';
const SIGNED_CONTRACTS_BUCKET = 'signed-contracts';

const getStoragePathCandidates = (value: string, bucket: string) => {
  const bucketPrefix = `${bucket}/`;
  const path = value.startsWith(bucketPrefix) ? value.slice(bucketPrefix.length) : value;
  return [...new Set([path, value])];
};

const resolvePromoterIdDocument = async (value?: string | null) => {
  if (!value) return null;

  for (const path of getStoragePathCandidates(value, PROMOTER_IDS_BUCKET)) {
    const signedFromPromoterBucket = await resolveSignedStorageUrl(
      PROMOTER_IDS_BUCKET,
      path,
      60 * 60
    );
    if (signedFromPromoterBucket) return signedFromPromoterBucket;
  }

  for (const path of getStoragePathCandidates(value, 'documents')) {
    const signedFromDocumentsBucket = await resolveSignedStorageUrl('documents', path, 60 * 60);
    if (signedFromDocumentsBucket) return signedFromDocumentsBucket;
  }

  return value.startsWith('http://') || value.startsWith('https://') ? value : null;
};

const resolveContractDocument = async (value?: string | null) => {
  if (!value) return null;

  for (const path of getStoragePathCandidates(value, SIGNED_CONTRACTS_BUCKET)) {
    const signedFromContractBucket = await resolveSignedStorageUrl(
      SIGNED_CONTRACTS_BUCKET,
      path,
      60 * 60
    );
    if (signedFromContractBucket) return signedFromContractBucket;
  }

  for (const path of getStoragePathCandidates(value, LEGAL_SIGNATURES_BUCKET)) {
    const signedFromLegalBucket = await resolveSignedStorageUrl(
      LEGAL_SIGNATURES_BUCKET,
      path,
      60 * 60
    );
    if (signedFromLegalBucket) return signedFromLegalBucket;
  }

  return value.startsWith('http://') || value.startsWith('https://') ? value : null;
};

export const PROFILE_DOCUMENT_LINKS: Array<{ key: ProfileDocumentLinkKey; label: string }> = [
  { key: 'headshot', label: 'Headshot' },
  { key: 'cv_photo', label: 'Full Body Photo' },
  { key: 'id_document', label: 'ID Document' },
  { key: 'passport_document', label: 'Passport Document' },
  { key: 'work_permit_document', label: 'Work Permit Document' },
  { key: 'cv_document', label: 'CV Document' },
  { key: 'bank_proof', label: 'Bank Proof' },
  { key: 'matric_certificate', label: 'Matric Certificate' },
  { key: 'highest_qualification_certificate', label: 'Highest Qualification Certificate' },
  { key: 'account_holder_authorisation', label: 'Account Holder Authorisation' },
  { key: 'onboarding_contract_pdf', label: 'Onboarding Contract (PDF)' },
  { key: 'onboarding_contract_signature', label: 'Onboarding Signature' },
  { key: 'latest_signed_contract_pdf', label: 'Latest Signed Contract' },
];

export const resolvePromoterProfileDocumentLinks = async (
  snapshot: PromoterProfileSnapshot
): Promise<ProfileDocumentLink[]> => {
  const passportSource =
    snapshot.passport_document ||
    (snapshot.identity_document_type === 'foreign_passport' ? snapshot.id_document : '');

  const [
    headshot,
    cvPhoto,
    idDocument,
    passportDocument,
    workPermitDocument,
    cvDocument,
    bankProof,
    matricCertificate,
    highestQualificationCertificate,
    accountHolderAuthorisation,
    onboardingPdf,
    onboardingSignature,
    latestSignedContract,
  ] = await Promise.all([
    resolvePromoterPhotoUrl(snapshot.headshot),
    resolvePromoterPhotoUrl(snapshot.cv_photo),
    resolvePromoterIdDocument(snapshot.id_document),
    resolvePromoterIdDocument(passportSource),
    resolvePromoterIdDocument(snapshot.work_permit_document),
    resolvePromoterIdDocument(snapshot.cv_document),
    resolveBankProofUrl(snapshot.bank_proof),
    resolvePromoterIdDocument(snapshot.matric_certificate),
    resolvePromoterIdDocument(snapshot.highest_qualification_certificate),
    resolvePromoterIdDocument(snapshot.account_holder_authorisation),
    resolveContractDocument(snapshot.onboarding_contract_pdf),
    resolveContractDocument(snapshot.onboarding_contract_signature),
    resolveContractDocument(snapshot.latest_signed_contract_pdf),
  ]);

  const urlMap: Record<ProfileDocumentLinkKey, string | null> = {
    headshot,
    cv_photo: cvPhoto,
    id_document: idDocument,
    passport_document: passportDocument,
    work_permit_document: workPermitDocument,
    cv_document: cvDocument,
    bank_proof: bankProof,
    matric_certificate: matricCertificate,
    highest_qualification_certificate: highestQualificationCertificate,
    account_holder_authorisation: accountHolderAuthorisation,
    onboarding_contract_pdf: onboardingPdf,
    onboarding_contract_signature: onboardingSignature,
    latest_signed_contract_pdf: latestSignedContract,
  };

  return PROFILE_DOCUMENT_LINKS.map((item) => {
    const url = urlMap[item.key] ?? null;
    return {
      key: item.key,
      label: item.label,
      url,
      state: url ? 'present' : 'missing',
    };
  });
};
